import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertType, BackButton, Spinner, TextArea, TextInput } from '../../components';
import { AppRoutes } from '../../constants';
import { useToast } from '../../hooks';
import { useGetPromptTemplateByIdQuery } from '../../services';
import { PromptTemplate } from '../../types';
import ManagePromptTemplateFooter from './ManagePromptTemplateFooter';
import PromptTemplateLiquidTags from './PromptTemplateLiquidTags';
import PromptTemplateSettings from './PromptTemplateSettings';

interface ManagePromptTemplatePageProps {
  /**
   * The prompt template to edit.
   * If provided, we use this template directly (e.g. when navigating from the list).
   * If not provided, we get the ID from the URL and fetch the prompt template.
   * If there is no ID, we are creating a new prompt template.
   */
  promptTemplate?: PromptTemplate;
  onBack?: () => void;
}

const ManagePromptTemplatePage = ({ promptTemplate: promptTemplateProp, onBack }: ManagePromptTemplatePageProps) => {
  const navigate = useNavigate();

  // Get the prompt template ID from the URL.
  // This is only used when directly navigating to this page via URL.
  // When navigating from the list, we use the promptTemplate prop instead.
  const { promptTemplateId } = useParams();

  // Only fetch the prompt template if:
  // 1. We have a promptTemplateId in the URL (direct navigation)
  // 2. We don't have a promptTemplate prop (not navigating from list)
  const { data: promptTemplateFromApi, isLoading } = useGetPromptTemplateByIdQuery(promptTemplateId || '', {
    skip: !promptTemplateId || !!promptTemplateProp,
  });

  const [cursorPosition, setCursorPosition] = useState(0);
  const [nameInput, setNameInput] = useState(promptTemplateProp?.name || '');
  const [newIsDefault, setNewIsDefault] = useState(promptTemplateProp?.default || false);
  const [promptInput, setPromptInput] = useState(promptTemplateProp?.prompt || '');
  const [selectedCategory, setSelectedCategory] = useState(promptTemplateProp?.category);
  const [selectedType, setSelectedType] = useState(promptTemplateProp?.type);
  const [isInsertingTag, setIsInsertingTag] = useState(false);

  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const { showToast } = useToast();

  const promptTemplate = promptTemplateProp || promptTemplateFromApi;

  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPromptInput(e.target.value);
    setCursorPosition(e.target.selectionStart);
  };

  const insertTagAtCursor = useCallback(
    (tag: string) => {
      setIsInsertingTag(true);
      // Insert the tag at the cursor position.
      const newValue = promptInput.slice(0, cursorPosition) + tag + promptInput.slice(cursorPosition);
      setPromptInput(newValue);
      // Set the cursor position to the end of the tag.
      const newCursorPosition = cursorPosition + tag.length;
      setCursorPosition(newCursorPosition);
    },
    [cursorPosition, promptInput]
  );

  // Navigate back to the prompt templates page.
  const handleBack = useCallback(() => {
    onBack?.();
    navigate(AppRoutes.PROMPT_TEMPLATES);
  }, [navigate, onBack]);

  // After inserting a tag, refocus the text area and set the cursor position to the end of the tag.
  useEffect(() => {
    if (textAreaRef.current && isInsertingTag) {
      const textarea = textAreaRef.current;
      const scrollPos = textarea.scrollTop; // Save current scroll position.

      textarea.focus();
      textarea.setSelectionRange(cursorPosition, cursorPosition);
      textarea.scrollTop = scrollPos; // Restore scroll position.

      setIsInsertingTag(false);
    }
  }, [cursorPosition, isInsertingTag]);

  useEffect(() => {
    if (promptTemplateId && !promptTemplate && !isLoading) {
      // If the prompt template ID is provided but the prompt template is not found,
      // navigate to the prompt templates page and show an error toast.
      navigate(AppRoutes.PROMPT_TEMPLATES);
      showToast({
        title: 'Prompt template not found',
        message: 'The prompt template you are trying to access does not exist.',
        type: AlertType.ERROR,
      });
    } else if (promptTemplateFromApi) {
      // Otherwise, if the prompt template was fetched from the API, set the state to the prompt template values.
      setNameInput(promptTemplateFromApi.name);
      setNewIsDefault(promptTemplateFromApi.default);
      setPromptInput(promptTemplateFromApi.prompt);
      setSelectedCategory(promptTemplateFromApi.category);
      setSelectedType(promptTemplateFromApi.type);
    }
  }, [promptTemplateId, promptTemplate, promptTemplateFromApi, isLoading, navigate, showToast]);

  const renderSidebar = (content: React.ReactNode) => (
    <div className="bg-base-200 min-w-64 max-w-64 rounded-lg p-4">{content}</div>
  );

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="bg-base-100 flex h-full flex-col">
      <div className="flex items-center gap-4 px-8 pt-4">
        <BackButton onBack={handleBack} />
        <TextInput
          placeholder="Prompt template name*"
          value={nameInput}
          onChange={(e) => setNameInput(e.target.value)}
        />
      </div>
      <div className="flex h-full justify-center overflow-y-auto p-8">
        <div className="flex h-full w-full gap-4">
          {renderSidebar(
            <PromptTemplateSettings
              newIsDefault={newIsDefault}
              promptTemplate={promptTemplate}
              setNewIsDefault={setNewIsDefault}
              setSelectedCategory={setSelectedCategory}
              setSelectedType={setSelectedType}
              selectedCategory={selectedCategory}
              selectedType={selectedType}
            />
          )}
          <TextArea
            fillContainer
            ref={textAreaRef}
            placeholder="Prompt template text*"
            value={promptInput}
            onChange={handleTextAreaChange}
            onSelect={(e) => setCursorPosition(e.currentTarget.selectionStart)}
          />
          {renderSidebar(<PromptTemplateLiquidTags onInsertTag={insertTagAtCursor} />)}
        </div>
      </div>
      <ManagePromptTemplateFooter
        promptTemplate={promptTemplate}
        nameInput={nameInput}
        newIsDefault={newIsDefault}
        promptInput={promptInput}
        selectedCategory={selectedCategory}
        selectedType={selectedType}
        handleBack={handleBack}
      />
    </div>
  );
};

export default ManagePromptTemplatePage;
